import React from 'react';
import { useSelector } from 'react-redux';
import { X } from 'lucide-react';

const inputClass = "w-full px-3 py-2 border border-[#C9C9C9] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";
const labelClass = "text-[12px] font-semibold text-[#5C5C5C] block mb-1.5";

const SWATCHES = ['#0176D3', '#04844B', '#EA001E', '#8E4EC6', '#DD7A01', '#181818'];
const FONTS = [
  { value: '', label: 'Default (System)' },
  { value: "'Georgia', serif", label: 'Georgia (Serif)' },
  { value: "'Courier New', monospace", label: 'Courier New (Mono)' },
  { value: "'Trebuchet MS', sans-serif", label: 'Trebuchet MS' },
];

/**
 * Form-level design settings — distinct from PropertiesPanel (which edits
 * one field). Everything here writes into schema.theme, a plain CSS-token
 * object consumed by FormRenderer via CSS custom properties, so the same
 * settings repaint the canvas preview, the builder's Preview modal, and the
 * live public form identically.
 */
export default function ThemePanel({ theme, onChange, onClose }) {
  const set = (key, value) => onChange({ ...theme, [key]: value });
  const companyLogoUrl = useSelector((state) => state.auth?.user?.logoUrl);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[440px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-xl p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <p className="text-[16px] font-bold text-[#2E2E2E]">Form Design</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelClass}>Branding</label>
            {companyLogoUrl ? (
              <label className="flex items-center gap-3 px-3 py-2 border border-[#D8DDE6] rounded-[4px] cursor-pointer hover:border-[#0176D3]/40">
                <input
                  type="checkbox"
                  checked={!!theme.showLogo}
                  onChange={(e) => onChange({ ...theme, showLogo: e.target.checked, logoUrl: e.target.checked ? companyLogoUrl : theme.logoUrl })}
                  className="accent-[#0176D3] w-4 h-4 shrink-0"
                />
                <img src={companyLogoUrl} alt="Company logo" className="h-6 max-w-[80px] object-contain shrink-0" />
                <span className="text-[12px] font-medium text-[#181818]">Show company logo at the top of this form</span>
              </label>
            ) : (
              <p className="text-[11px] text-[#706E6B]">
                No company logo on file — upload one in Settings to use it here.
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>Primary Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={theme.primaryColor || '#0176D3'}
                onChange={(e) => set('primaryColor', e.target.value)}
                className="w-9 h-9 rounded-[4px] border border-[#D8DDE6] cursor-pointer shrink-0"
              />
              <div className="flex gap-1.5 flex-wrap">
                {SWATCHES.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    onClick={() => set('primaryColor', hex)}
                    className="w-6 h-6 rounded-full border border-black/10"
                    style={{ backgroundColor: hex }}
                    title={hex}
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className={labelClass}>Background Color</label>
            <input
              type="color"
              value={theme.backgroundColor || '#FFFFFF'}
              onChange={(e) => set('backgroundColor', e.target.value)}
              className="w-9 h-9 rounded-[4px] border border-[#D8DDE6] cursor-pointer"
            />
          </div>

          <div>
            <label className={labelClass}>Font Family</label>
            <select className={inputClass} value={theme.fontFamily || ''} onChange={(e) => set('fontFamily', e.target.value)}>
              {FONTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>

          <div>
            <label className={labelClass}>Corner Roundness ({theme.borderRadius ?? 4}px)</label>
            <input
              type="range"
              min={0}
              max={16}
              value={theme.borderRadius ?? 4}
              onChange={(e) => set('borderRadius', Number(e.target.value))}
              className="w-full accent-[#0176D3]"
            />
          </div>

          <div>
            <label className={labelClass}>Submit Button Label</label>
            <input
              className={inputClass}
              value={theme.submitLabel || ''}
              onChange={(e) => set('submitLabel', e.target.value)}
              placeholder="Submit"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
